import { z } from 'zod';
import { apiFor } from '@/api/client';

/*
 * The store-allocation round trip.
 *
 * The portal exports the collection winners; the client adds the store and its
 * format and uploads the file back here. Uploading only STORES the file — every
 * line of it, for the log — and a separate step pushes the pending lines to the
 * mini-games API. They are separate so a file is never lost because the API
 * happened to be down.
 */

const countsSchema = z.object({ pending: z.number(), sent: z.number(), invalid: z.number(), duplicate: z.number().optional() });

const uploadSchema = z.object({
  id: z.number(),
  filename: z.string(),
  sizeBytes: z.number(),
  rowCount: z.number(),
  uploadedBy: z.string().nullable(),
  uploadedAt: z.string().nullable(),
  /** Which column each field was read from, and whether by header or by position. */
  columnMap: z.record(z.string(), z.unknown()).nullable(),
  counts: countsSchema,
  error: z.string().nullable(),
});

const apiReadinessSchema = z.object({ ready: z.boolean(), missing: z.array(z.string()) });

export const allocationUploadEnvelope = z.object({
  data: uploadSchema,
  meta: z.object({ already_uploaded: z.boolean().optional(), api: apiReadinessSchema.optional() }).partial(),
});

export const allocationListEnvelope = z.object({
  data: z.array(uploadSchema),
  meta: z.object({ api: apiReadinessSchema.optional() }).partial(),
});

const rowSchema = z.object({
  id: z.number(),
  line_no: z.number(),
  /** The line exactly as it arrived, keyed by the file's own headers. */
  raw: z.record(z.string(), z.unknown()),
  lot: z.string().nullable(),
  magasins: z.string().nullable(),
  format: z.string().nullable(),
  medium_code: z.string().nullable(),
  /** Normalised redemption time — what identifies the record across files. */
  redeemed_at: z.string().nullable().optional(),
  status: z.enum(['pending', 'sent', 'invalid', 'duplicate']),
  attempts: z.number(),
  sent_at: z.string().nullable(),
  error: z.string().nullable(),
});

export const allocationRowsEnvelope = z.object({
  data: z.array(rowSchema),
  meta: z.object({ upload: uploadSchema, limit: z.number() }),
});

export const processResultEnvelope = z.object({
  data: z.object({
    attempted: z.number(),
    sent: z.number(),
    failed: z.number(),
    batches: z.number(),
    errors: z.array(z.object({ lines: z.number(), permanent: z.boolean(), error: z.string().optional() })),
    /** Rows skipped because their redemption had already been sent. */
    duplicates: z.number().optional(),
    /** Set when nothing could be attempted — today, the API key is not configured. */
    blocked: z.string().optional(),
  }),
  meta: z.object({ api: apiReadinessSchema.optional(), message: z.string().optional() }).partial(),
});

export type AllocationUpload = z.infer<typeof uploadSchema>;
export type AllocationRow = z.infer<typeof rowSchema>;
export type ProcessResult = z.infer<typeof processResultEnvelope>;

export async function uploadAllocationFile(reportId: string, baseUrl: string, file: File) {
  const form = new FormData();
  form.set('file', file);
  const raw = await apiFor(baseUrl).post(`reports/${reportId}/allocations`, { body: form, timeout: 120_000 }).json();
  return allocationUploadEnvelope.parse(raw);
}

export async function listAllocationUploads(reportId: string, baseUrl: string) {
  const raw = await apiFor(baseUrl).get(`reports/${reportId}/allocations`).json();
  return allocationListEnvelope.parse(raw);
}

export async function getAllocationRows(reportId: string, baseUrl: string, uploadId: number, status?: string) {
  const q = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
  const raw = await apiFor(baseUrl).get(`reports/${reportId}/allocations/${uploadId}${q}`).json();
  return allocationRowsEnvelope.parse(raw);
}

/** Push the pending lines to the mini-games API. Safe to call repeatedly. */
export async function processAllocations(reportId: string, baseUrl: string, uploadId?: number) {
  const raw = await apiFor(baseUrl)
    .post(`reports/${reportId}/allocations/process`, { json: uploadId ? { uploadId } : {}, timeout: 120_000 })
    .json();
  return processResultEnvelope.parse(raw);
}
